<template>
  <div id="map" :class="{ 'is-active': mapLoaded }">
    <div class="wrapper">
      <layers-section
          :list="services">
      </layers-section>
      <draw @drawMode="drawMode" :isDrawing="getDrawCircleMode"/>
      <legend-section :services="services"></legend-section>
      <switcherThemes :map="map"/>

    </div>
  </div>

</template>

<script>

import {
  MAP_SERVICES_CLUSTER_LAYER,
  MAP_SERVICES_UNCLUSTER_LAYER,
  MAP_SERVICES_SOURCE_ID,
  MAP_ADDRESS_SOURCE_ID,
  MAP_ADDRESS_CLUSTER_LAYER,
  MAP_ADDRESS_UNCLUSTER_LAYER,
  MAP_SERVICES_UNCLUSTER_ICON,
  mapIcons
} from '@/data/variables'
import removePopup from '@/mixins/removePopup.vue'
import i18n from '@/i18n.js'
import store from '@/store/index.js'
import PopupContent from './popup/popup.vue'
import circlePopup from '@/components/map/circle/circlePopup.vue'
import popupSamePositions from '@/components/map/popup/popupSamePositions.vue'
import {mapGetters} from 'vuex'
import mapboxgl from 'mapbox-gl'
import legend from './legend.vue'
import layers from './layers.vue'
import draw from './drawCircle.vue'
import switcherThemes from './mapSwitcherThemes.vue'
import throttle from '@/helpers/throttle.js'
import mapServicesClusters from './mapServicesClusters.js'
import mapAddressesClusters from './mapAddressesClusters.js'
import {addImages} from '@/helpers/map'
import eventBus from '@/helpers/event-bus'
import {RulerControl} from 'mapbox-gl-controls';

export default {
  components: {
    'legend-section': legend,
    'layers-section': layers,
    draw,
    switcherThemes
  },
  data() {
    return {
      rulerControl: new RulerControl({
        textAllowOverlap: true,
      }),
      map: null,
      mapboxgl: null,
      zoom: 4,
      center: [11.55, 48.13],
      mapLoaded: false,
      radius: {
        center_latitude: 0,
        center_longitude: 0,
        radius: 0
      },
      services: [],
    }
  },
  mixins: [removePopup, mapServicesClusters, mapAddressesClusters],
  methods: {
    onMapClusterClick(event, source, layer) {
      const map = this.map;
      const features = map.queryRenderedFeatures(event.point, {layers: [layer]});
      const clusterId = features[0].properties.cluster_id;
      map.getSource(source).getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error) {
          return;
        }
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        });
      });
    },
    getModalData(event, layers, getAllCollection = false) {
      const elements = this.map.queryRenderedFeatures(event.point, {layers: [layers]});
      const features = getAllCollection ? [...elements] : elements[0];
      const coordinates = elements[0].geometry.coordinates.slice();
      while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360
      }
      return {features, coordinates}
    },
    async drawMode(val) {
      this.$store.commit('SET_DRAWING_CIRCLE_MODE', val);
      if (!this.getDrawCircleMode) {
        this.removePopupsHandler()
        this.$store.dispatch('REMOVE_DRAWING_CIRCLE');
        await this.$store.dispatch('FETCH_DRAWING_CIRCLE_DATA')
      }
    },
    deg2rad(deg) {
      return deg * (Math.PI / 180)
    },
    getDistanceFromLatLonInKm(lat2, lon2) {
      let lat1 = this.radius.center_latitude,
          lon1 = this.radius.center_longitude;
      if ((lat1 == lat2) && (lon1 == lon2)) {
        return 0;
      } else {
        const R = 6371; // Radius of the earth in km
        const dLat = this.deg2rad(lat2 - lat1);  // deg2rad below
        const dLon = this.deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c; // Distance in km
        return d;
      }
    },
    onMove(e) {
      let {lng, lat} = e.lngLat;
      this.radius.radius = Math.round(this.getDistanceFromLatLonInKm(lat, lng))
      this.UPDATE_DRAWING_CIRCLE
    },
    async onUp() {
      this.map.off('mousemove', this.onMove);
      this.map.off('touchmove', this.onMove);
      if (this.radius.radius === 0) {
        return
      }
      this.$store.commit('SET_DRAWING_CIRCLE', true)
      this.canvas.style.cursor = 'grab';
      await this.$store.dispatch('FETCH_DRAWING_CIRCLE_DATA', {circle: this.radius})
    },

    async fetchLocations() {
      return await this.$store.dispatch('fetchLocations')
    },
    async fetchAddresses() {
      return await this.$store.dispatch('fetchAddresses')
    },
    async fetchLayers() {
      return await this.$store.dispatch('fetchLayers').then(response => {
        this.services = response.data.data.layers
      })
    },
    createMap() {
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
      // init the map
      this.map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/streets-v11',
        attributionControl: false,
        center: this.center,
        zoom: this.zoom,
      });

      this.addControls(); // add default controls
      this.$store.commit('CREATE_DRAWING_CIRCLE')
      this.map.on("load", (event) => {
        let vm = this;
        this.mapLoaded = true;
        this.$store.dispatch('SAVE_MAP', event.target)
        let map = vm.map;
        this.canvas = map.getCanvasContainer();

        addImages(map, mapIcons).then(() => {
          this.addClientsAddressesToMap(); // client addresses clusters
        });

        this.addMarkersToMap(); // add services clusters

        map.on('ruler.change', function () {
          map.moveLayer('controls-layer-symbol')
          map.moveLayer('controls-layer-line')
        })

        map.addSource('DRAW_CIRCLE', {
          type: 'geojson',
          data: this.getDrawCircleData,
        });

        map.addLayer({
          "id": "DRAW_CIRCLE",
          "type": "circle",
          "source": "DRAW_CIRCLE",
          "paint": {
            "circle-radius": [
              "interpolate",
              ["exponential", 2],
              ["zoom"],
              0, 0,
              22,
              ["/", ["/", ["get", "radius"], 0.01866137981], ["cos", ["*", ['get', 'lat'], ["/", ["pi"], 180]]]]
            ],
            'circle-color': 'rgba(218, 95, 38, 0.05)',
            'circle-stroke-color': '#DA5F26',
            'circle-stroke-width': 2,
          }
        });

        map.addSource('DRAW_DRIWING_ZONE', {
          type: 'geojson',
          data: {
            "features": [],
            "type": "FeatureCollection"
          }
        });
        map.addLayer({
          'id': 'DRAW_DRIWING_FILL',
          'type': 'fill',
          'source': 'DRAW_DRIWING_ZONE',
          'paint': {
            'fill-color': 'rgba(252, 152, 39, 0.06)',
          }
        });
        map.addLayer({
          'id': 'DRAW_DRIWING_LINE',
          'type': 'line',
          'source': 'DRAW_DRIWING_ZONE',
          'layout': {
            'line-join': 'round',
            'line-cap': 'round'
          },
          'paint': {
            'line-color': '#FC9827',
            'line-width': 2,
          }
        });


        // zoom on cluster
//                       map.on('click', MAP_SERVICES_CLUSTER_LAYER, event => this.onMapClusterClick(event, MAP_SERVICES_SOURCE_ID, MAP_SERVICES_CLUSTER_LAYER));
//                       map.on('click', MAP_ADDRESS_CLUSTER_LAYER, event => this.onMapClusterClick(event, MAP_ADDRESS_SOURCE_ID, MAP_ADDRESS_CLUSTER_LAYER));

        // create modals
        map.on('click', 'DRAW_CIRCLE', event => this.createCircleModal(event, 'DRAW_CIRCLE'));
        map.on('click', MAP_SERVICES_UNCLUSTER_LAYER, event => this.createUnclusteredServicesModal(event, MAP_SERVICES_UNCLUSTER_LAYER));
        map.on('click', MAP_ADDRESS_UNCLUSTER_LAYER, event => this.createClientAddressModal(event, MAP_ADDRESS_UNCLUSTER_LAYER, this.rulerControl));
        map.on('click', MAP_SERVICES_UNCLUSTER_ICON, event => this.createUnclusteredSamePositionModal(event, MAP_SERVICES_UNCLUSTER_ICON));
        map.on('mouseenter', MAP_ADDRESS_UNCLUSTER_LAYER, () => this.canvas.style.cursor = 'pointer')
        map.on('mouseleave', MAP_ADDRESS_UNCLUSTER_LAYER, () => this.canvas.style.cursor = '')


        // draw unclustered services
        map.on('move', event => this.onMapMoveEnd(event));
        map.on('moveend', event => this.map.once('idle', () => this.redrawMarkers()));


        map.on('mousedown', (e) => {
          if (!this.getDrawCircleMode || this.isDrawCircle) return;
          let {lat, lng} = e.lngLat;
          vm.radius = {
            center_latitude: lat,
            center_longitude: lng,
            radius: 0
          };
          e.preventDefault();
          this.canvas.style.cursor = 'move';
          map.on('mousemove', vm.onMove);
          map.once('mouseup', vm.onUp);
        });
//
//                       map.on('touchstart', 'polygon', function(e) {
//                           let {lng, lat, id} = e.features[0].properties;
//                           vm.radius = {lng,lat,id};
////                           if (e.features.length !== 1) return;
//
//                            // Prevent the default map drag behavior.
//                           e.preventDefault();
//
//                           map.on('touchmove', vm.onMove);
//                           map.once('touchend', vm.onUp);
//                       });

      });

    },
    createUnclusteredSamePositionModal(event, layer) {
      let getCollection = true;
      let {features, coordinates} = this.getModalData(event, layer, getCollection)
      let popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        offset: {
          'top': [-2, 33],
          'top-left': [5, 33],
          'top-right': [-5, 33],
          'bottom': [0, -35],
          'bottom-left': [0, -25],
          'bottom-right': [-10, -25],
          'left': [25, 0],
          'right': [-25, 0],
        }
      })
          .setLngLat([coordinates[0], coordinates[1]])
          .setHTML('<div id="popupSamePositions"></div>')
          .setMaxWidth('288px')
          .addTo(this.map);

      new popupSamePositions({
        propsData: {
          features: features,
          popup: popup
        },
      }).$mount('#popupSamePositions')
    },
    createUnclusteredServicesModal(event, layer) {
      let {features, coordinates} = this.getModalData(event, layer)
      this.generateUnclusteredServiceModal(features, coordinates)
    },
    createUnclusteredServiceModal(payload) {
      let {features, coordinates} = payload;
      let flyTo = false;
      this.generateUnclusteredServiceModal(features, coordinates, flyTo)
    },
    generateUnclusteredServiceModal(features, coordinates, flyTo = true) {
      if (this.rulerControl.isMeasuring) {
        //ignore opening modals if measuring in progress
        return
      }

      const {name, address, id} = features.properties;
      this.$store.dispatch('SET_LOCATION', {name, address, id})
      const popUps = document.getElementsByClassName('mapboxgl-popup');

      if (popUps.length > 0) [...popUps].forEach(popup => popup.remove())

      if (flyTo) {
        this.flyTo(coordinates)
      }

      new mapboxgl.Popup({
        closeOnClick: true, offset: {
          'top': [-2, 33],
          'top-left': [5, 33],
          'top-right': [-5, 33],
          'bottom': [0, -35],
          'bottom-left': [0, -35],
          'bottom-right': [-10, -35],
          'left': [18, 0],
          'right': [-18, 0],
        }
      },)
          .setLngLat([coordinates[0], coordinates[1]])
          .setHTML('<div id="vue-popup-content"></div>')
          .setMaxWidth('320px')
          .addTo(this.map);

      new PopupContent({
        i18n,
        store,
        propsData: {features: features.properties},
      }).$mount('#vue-popup-content')
    },
    flyTo(coordinates) {
      this.map.flyTo({
        zoom: 12,
        speed: 0.55, // make the flying slow
        curve: 2, // change the speed at which it zooms out
        center: [coordinates[0], coordinates[1]],
        offset: [0, 140],
      });
    },
    createCircleModal(event, layer) {
      let {features, coordinates} = this.getModalData(event, layer)
      let circleRadius = Math.round(this.getDrawCircleRadius);

      var linearOffset = Math.round(Math.sqrt(0.5 * Math.pow(circleRadius, 2)));

      let popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        offset: [0, 0]
      })
          .setLngLat([coordinates[0], coordinates[1]])
          .setHTML('<div id="circlePopup"></div>')
          .setMaxWidth('200px')
          .addTo(this.map);

      new circlePopup({
        i18n,
        store,
        propsData: {
          circle: {
            center_latitude: this.radius.center_latitude,
            center_longitude: this.radius.center_longitude,
            radius: +this.getDrawCircleRadius
          },
          popup: popup
        },
      }).$mount('#circlePopup')
    },
    addControls() {
      this.map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
      this.map.addControl(this.rulerControl, 'bottom-right');
    },
  },
  computed: {
    ...mapGetters([
      'getEarthquakes',
      'getAddresses',
      'getDrawMarkers',
      'isDrawCircle',
      'getDrawCircleData',
      'getDrawCircleMode',
      'getDrawCircleRadius'
    ]),
    UPDATE_DRAWING_CIRCLE() {
      let DELAY = 1000;
      return throttle(this.$store.dispatch('UPDATE_DRAWING_CIRCLE', this.radius), DELAY);
    }
  },
  mounted() {
    this.createMap();
    eventBus.$on('createServiceModal', (payload) => this.createUnclusteredServiceModal(payload))
  },
  beforeDestroy() {
    this.$store.commit('SET_DRAWING_CIRCLE_MODE', false)
    this.$store.commit('SET_DRAWING_CIRCLE', false)
    this.map = null;
    eventBus.$off('createServiceModal')
  },
  async created() {
    let services = localStorage.getItem('services') ? JSON.parse(localStorage.getItem('services')).data : null
    if (!services) {
      await this.fetchLayers();
    } else {
      this.services = services
    }
    Promise.all([
      this.fetchLocations(),
      this.fetchAddresses()
    ])
  }
}
</script>

<style lang="sass" scoped="">
#map
  width: 100%
  height: calc(100% - 80px)
  position: absolute
  top: 80px
  left: 0
  bottom: 0
  z-index: 1
  opacity: 0
  visibility: hidden
  overflow: hidden
  +tr(opacity .3s ease)
  overflow: hidden

  &.is-active
    opacity: 1
    visibility: visible

.wrapper
  position: absolute
  top: 40px
  right: 30px

.btn-legend
  position: absolute
  top: 0
  right: 0
  z-index: 222
</style>